import { useState, useEffect, useRef } from 'react';
import { createLogger } from '@/lib/logger';
import Constants from 'expo-constants';
import { View, Text, StyleSheet, TouchableOpacity, TextInput } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { StatusBar } from 'expo-status-bar';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { supabase } from '@/lib/supabase';
import { CHALLENGE_SCORE_COLUMN_V1 } from '@/lib/challenges/score';
import { useRouter } from 'expo-router';
import { FLAGS } from '@/lib/featureFlags';

const log = createLogger('CREATE');

export default function CreateChallenge() {
  const { profile } = useAuth();
  const { theme } = useTheme();
  const router = useRouter();

  // V1: Challenges disabled — redirect to home
  useEffect(() => {
    if (!FLAGS.V1_CHALLENGES_ENABLED) {
      router.replace('/(tabs)/home');
    }
  }, [router]);

  const [lift, setLift] = useState<'bench'|'squat'|'deadlift'>('bench');
  const [metricType, setMetricType] = useState<'e1rm'|'meet_total'>('e1rm');
  const [duration, setDuration] = useState<number>(72);
  const [title, setTitle] = useState<string>('');
  const [isGymAdmin, setIsGymAdmin] = useState(false);
  const [issuerOption, setIssuerOption] = useState<'duel'|'gym'>('duel');
  const issuerDefaultAppliedRef = useRef(false);
  const DEV_ADMIN_MODE = (Constants.expoConfig as any)?.extra?.EXPO_PUBLIC_DEV_ADMIN_MODE === 'true' || process.env.EXPO_PUBLIC_DEV_ADMIN_MODE === 'true';
  const [issuerGymName, setIssuerGymName] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const durations = [24, 48, 72, 168, 720];
  const durationLabels: Record<number,string> = {24:'24h',48:'48h',72:'72h',168:'7d',720:'30d'};

  const create = async () => {
    if (isSubmitting) return;
    setError(null);
    if (!profile) {
      setError('Not signed in');
      return;
    }
    if (!profile.gym_id) {
      setError('Set a home gym first.');
      return;
    }

    if (!profile.id) {
      setError('Profile not loaded');
      return;
    }

    setSaving(true);
    setIsSubmitting(true);
    try {
      const now = new Date();
      const ends = new Date(now.getTime() + duration * 60 * 60 * 1000);
      const defaultTitle = metricType === 'meet_total'
        ? `Meet Total (B/S/D) — ${durationLabels[duration]}`
        : `${lift.charAt(0).toUpperCase() + lift.slice(1)} E1RM — ${durationLabels[duration]}`;
      const t = title.trim() || defaultTitle;
      const payload: any = {
        title: t,
        scope_type: 'gym',
        scope_id: profile.gym_id,
        starts_at: now.toISOString(),
        ends_at: ends.toISOString(),
        created_by: profile.id,
      };

      // Determine issuer_type based on gym-admin toggle when available
      // If DEV mode is off, non-gym-admins are forced to DUEL (admin) and gym admins may choose Gym
      if (isGymAdmin) {
        // Ensure only allowed issuer options are respected when DEV mode is off
        const opt = issuerOption;
        if (opt === 'gym') {
          payload.issuer_type = 'gym';
          payload.issuer_gym_id = profile.gym_id;
        } else {
          payload.issuer_type = 'admin';
          payload.issuer_gym_id = null;
        }
      } else {
        // Non-gym-admin users: when DEV_ADMIN_MODE is off, default to DUEL (admin)
        if (!DEV_ADMIN_MODE) {
          payload.issuer_type = 'admin';
          payload.issuer_gym_id = null;
        } else {
          // In DEV mode, preserve previous behaviour (admins create as admin, others as gym)
          payload.issuer_type = profile.is_admin ? 'admin' : 'gym';
          payload.issuer_gym_id = null;
        }
      }

      if (metricType === 'meet_total') {
        payload.metric_type = 'meet_total';
        payload.lift_key = null;
        payload.config = { lifts: ['bench','squat','deadlift'], scoring: 'sum_best_e1rm' };
      } else {
        payload.metric_type = CHALLENGE_SCORE_COLUMN_V1;
        payload.lift_key = lift;
        payload.config = {};
      }

      if (typeof __DEV__ !== 'undefined' && __DEV__) {
        log.info('DEBUG: creating challenge payload', { ...payload, created_by: payload.created_by ? '<set>' : '<missing>' });
      }

      const { error: insertError } = await supabase.from('challenges').insert([payload]);
      if (insertError) {
        log.error('Create challenge failed', insertError);
        setError(insertError.message || 'Failed to create challenge');
        setSaving(false);
        setIsSubmitting(false);
        return;
      }

      // Navigate back and refresh only on successful insert
      router.push('/(profile)/challenges');
    } catch (e: any) {
      log.error('Create challenge exception', e);
      if (typeof __DEV__ !== 'undefined' && __DEV__) {
        try { log.info('Create exception details', e); } catch {}
      }
      setError('Failed to create challenge');
    } finally {
      setSaving(false);
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        if (!profile?.id || !profile?.gym_id) return;
        if (__DEV__) console.debug('[CreateChallenge] profile loaded', { profileId: profile.id, gymId: profile.gym_id });

        const { data, error } = await supabase
          .from('gym_admins')
          .select('gym_id,profile_id')
          .eq('gym_id', profile.gym_id)
          .eq('profile_id', profile.id)
          .maybeSingle();

        if (__DEV__) console.debug('[CreateChallenge] gym_admins query result', { data, error });
        if (mounted) {
          setIsGymAdmin(!!data);
          if (data) {
            // Default issuer selection to Gym for gym admins — only apply once and do not override user changes
            if (!issuerDefaultAppliedRef.current) {
              setIssuerOption('gym');
              issuerDefaultAppliedRef.current = true;
            }
          }
        }

        // Fetch gym name for issuer display (cache per screen)
        try {
          const { data: g } = await supabase.from('gyms').select('id,name').eq('id', profile.gym_id).maybeSingle();
          if (mounted) setIssuerGymName(g?.name ?? null);
        } catch {
          if (mounted) setIssuerGymName(null);
        }
      } catch {
        if (mounted) setIsGymAdmin(false);
      }
    })();
    return () => { mounted = false; };
  }, [profile?.id, profile?.gym_id]);

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: theme.colors.background }} edges={['top']}>
      <StatusBar style="light" />
      <View style={[styles.headerRow, { backgroundColor: theme.colors.background }]}>
        <View style={{ width: 48 }} />
        <View style={styles.headerTitleWrap}>
          <Text style={[styles.headerTitleText, { color: theme.colors.text }]}>Create Challenge</Text>
        </View>
        <View style={{ width: 48 }} />
      </View>

      <View style={[styles.container, { padding: 16 }]}> 
        <Text style={[styles.label, { color: theme.colors.text }]}>Lift</Text>
          <View style={{ flexDirection: 'row', gap: 12, marginBottom: 12 }}>
            <TouchableOpacity onPress={() => setMetricType('e1rm')} style={[styles.pill, { backgroundColor: metricType === 'e1rm' ? theme.colors.accent : theme.colors.surface }]}>
              <Text style={{ color: metricType === 'e1rm' ? theme.colors.text : theme.colors.textSecondary }}>Single lift (E1RM)</Text>
            </TouchableOpacity>
            <TouchableOpacity onPress={() => setMetricType('meet_total')} style={[styles.pill, { backgroundColor: metricType === 'meet_total' ? theme.colors.accent : theme.colors.surface }]}>
              <Text style={{ color: metricType === 'meet_total' ? theme.colors.text : theme.colors.textSecondary }}>Meet Total (B/S/D)</Text>
            </TouchableOpacity>
          </View>

          {metricType === 'e1rm' ? (
            <View style={{ flexDirection: 'row', gap: 12, marginBottom: 12 }}>
              {(['bench','squat','deadlift'] as const).map(k => (
                <TouchableOpacity key={k} onPress={() => setLift(k)} style={[styles.pill, { backgroundColor: lift===k ? theme.colors.accent : theme.colors.surface }]}>
                  <Text style={{ color: lift===k ? theme.colors.text : theme.colors.textSecondary }}>{k.charAt(0).toUpperCase() + k.slice(1)}</Text>
                </TouchableOpacity>
              ))}
            </View>
          ) : null}

        <Text style={[styles.label, { color: theme.colors.text }]}>Duration</Text>
        <View style={{ flexDirection: 'row', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
          {durations.map(d => (
            <TouchableOpacity key={d} onPress={() => setDuration(d)} style={[styles.pill, { backgroundColor: duration===d ? theme.colors.accent : theme.colors.surface }]}>
              <Text style={{ color: duration===d ? theme.colors.text : theme.colors.textSecondary }}>{durationLabels[d]}</Text>
            </TouchableOpacity>
          ))}
        </View>

        <Text style={[styles.label, { color: theme.colors.text }]}>Title (optional)</Text>
        <TextInput value={title} onChangeText={setTitle} placeholder="Optional title" placeholderTextColor={theme.colors.textSecondary} style={[styles.input, { color: theme.colors.text, borderColor: theme.colors.border, backgroundColor: theme.colors.surface }]} />

        {/* Read-only issuer line */}
        {(() => {
          const displayedIssuer = isGymAdmin ? (issuerOption === 'gym' ? (issuerGymName || 'Gym') : 'DUEL') : 'DUEL';
          return <Text style={[styles.label, { color: theme.colors.textSecondary, marginTop: 12 }]}>{`Issued by ${displayedIssuer}`}</Text>;
        })()}

        {/* Issuer selector only visible to gym admins in DEV mode (hidden otherwise) */}
        {isGymAdmin && DEV_ADMIN_MODE ? (
          <View style={{ flexDirection: 'row', gap: 12, marginTop: 8 }}>
            <TouchableOpacity onPress={() => setIssuerOption('duel')} style={[styles.pill, { backgroundColor: issuerOption === 'duel' ? theme.colors.accent : theme.colors.surface }]}>
              <Text style={{ color: issuerOption === 'duel' ? theme.colors.text : theme.colors.textSecondary }}>DUEL</Text>
            </TouchableOpacity>
            <TouchableOpacity onPress={() => setIssuerOption('gym')} style={[styles.pill, { backgroundColor: issuerOption === 'gym' ? theme.colors.accent : theme.colors.surface }]}>
              <Text style={{ color: issuerOption === 'gym' ? theme.colors.text : theme.colors.textSecondary }}>Gym</Text>
            </TouchableOpacity>
          </View>
        ) : null}

        {error ? <Text style={{ color: theme.colors.error, marginTop: 8 }}>{error}</Text> : null}

        <View style={{ marginTop: 16 }}>
          <TouchableOpacity onPress={create} style={[styles.createButton, { backgroundColor: theme.colors.accent, opacity: saving ? 0.6 : 1 }]} disabled={saving}>
            <Text style={{ color: theme.colors.text }}>{saving ? 'Creating...' : 'Create Challenge'}</Text>
          </TouchableOpacity>
        </View>
      </View>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  headerRow: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 16, paddingVertical: 8, minHeight: 44 },
  headerButton: { padding: 8, minWidth: 48, minHeight: 44, justifyContent: 'center' },
  headerButtonText: { fontSize: 15, fontWeight: '600' },
  headerTitleWrap: { flex: 1, alignItems: 'center' },
  headerTitleText: { fontSize: 15, fontWeight: '700' },
  container: { flex: 1 },
  label: { fontSize: 13, marginBottom: 8 },
  pill: { paddingVertical: 8, paddingHorizontal: 12, borderRadius: 2, borderWidth: 1 },
  input: { borderWidth: 1, borderRadius: 2, paddingHorizontal: 12, paddingVertical: 10 },
  createButton: { paddingVertical: 12, paddingHorizontal: 16, borderRadius: 2, alignItems: 'center' },
});
